import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    Check,
    CircleDot,
    CopyPlus,
    ExternalLink,
    FileType2,
    FolderOpen,
    HardDrive,
    Info,
    Loader2,
    Monitor,
    PencilLine,
    Save,
    X,
    type LucideIcon
} from "lucide-react";
import { useAccessToken } from "@/auth/useAccessToken";
import { canSaveInPlace, driveApi, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { cn } from "@/lib/utils";
import { ShellHeaderPortal } from "@/features/shell/ShellHeader";
import type { PdfViewerHandle } from "./PdfViewerHost";
import { DrivePickerDialog, OpenChoiceDialog, SaveAsDialog, type SaveAsChoice } from "./DriveDialogs";
import { CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount } from "./comments/CommentsPanel";
import { pdfCommentsAdapter } from "./comments/pdfComments";
import { EditToolbar, ModeSwitch } from "./edit/EditToolbar";
import { FreeTextMiniToolbar } from "./edit/FreeTextMiniToolbar";
import {
    EDIT_COVER_SUBJECT,
    type DrawTool,
    type EditTool,
    type FreeTextStyle,
    type PageRect,
    type PdfMode,
    type ShapeKind,
    type ViewerChange
} from "./edit/editTypes";

const PdfViewerHost = lazy(() =>
    import("./PdfViewerHost").then((m) => ({ default: m.PdfViewerHost }))
);

/** clean = nothing to save; dirty = annotations/forms changed since open or the last save. */
type SaveStatus = "clean" | "dirty" | "saving" | "failed";

interface OpenDoc {
    name: string;
    /** The Drive file it was opened from / last saved to; null = a local file. */
    drive: DriveItem | null;
}

type DialogState =
    | { kind: "open-choice" }
    | { kind: "drive-picker" }
    | { kind: "save-as"; target: "drive" | "computer" };

const DRIVE_WEB_URL = (import.meta.env.VITE_DRIVE_WEB_URL ?? "").trim() || "http://localhost:5240";

/** Images placed with Edit → Image. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

function plural(n: number, word: string): string {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * Athavita PDF: view, annotate, fill and sign a PDF, then save it back to
 * Drive. `?fileId=<id>` opens a Drive file (Drive's "Open in PDF editor")
 * and stays in the URL for whatever file is open. Saving writes a new
 * version when the user owns or edits the file, otherwise Save As makes a
 * copy in a My Drive folder they choose.
 *
 * View / Edit: View is the viewer as it always was. Edit adds the red
 * "Editing" bar — Edit text (cover + new text), Add text, Image, Shape, Sign,
 * Form fields, Pages — counts the edits, and Save burns them into the page
 * (Drive BFF POST /api/pdf/flatten) before saving the new version. Leaving
 * Edit with edits discards them.
 */
export function PdfPage() {
    const [params, setParams] = useSearchParams();
    const fileIdParam = params.get("fileId");
    const openFileUrl = params.get("openFile");
    const openFileName = params.get("fileName");
    const token = useAccessToken();
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const hasToken = Boolean(token);

    const viewerRef = useRef<PdfViewerHandle>(null);
    const [viewerReady, setViewerReady] = useState(false);
    const [doc, setDoc] = useState<OpenDoc | null>(null);
    const docRef = useRef(doc);
    docRef.current = doc;
    const [status, setStatus] = useState<SaveStatus>("clean");
    const statusRef = useRef(status);
    statusRef.current = status;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialog, setDialog] = useState<DialogState | null>(null);

    /** Drive id whose bytes are in the viewer (or were last attempted). */
    const loadedIdRef = useRef<string | null>(null);
    /** Loading a document fires annotation events of its own — not user edits. */
    const ignoreChangesUntil = useRef(0);
    const changedWhileSaving = useRef(false);
    const urlOpened = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Bumped by every Drive / URL open and by Close — a stale open drops its result. */
    const openSeqRef = useRef(0);
    /** Settles when the save in flight finishes — Close waits for it. */
    const saveInFlightRef = useRef<Promise<void> | null>(null);

    /** Keeps ?fileId= pointing at the open Drive file (and drops the one-shot openFile params). */
    const setFileParam = useCallback(
        (id: string | null) => {
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (id) next.set("fileId", id);
                    else next.delete("fileId");
                    next.delete("openFile");
                    next.delete("fileName");
                    return next;
                },
                { replace: true }
            );
        },
        [setParams]
    );

    // ---------- Edit mode state ----------

    const [mode, setMode] = useState<PdfMode>("view");
    const modeRef = useRef<PdfMode>("view");
    const [edits, setEdits] = useState(0);
    const editsRef = useRef(0);
    const [tool, setToolState] = useState<EditTool>("none");
    const toolRef = useRef<EditTool>("none");
    const [formOpen, setFormOpen] = useState(false);
    const [hint, setHint] = useState<string | null>(null);
    /** The text box selected in Edit mode — the mini toolbar follows it. */
    const [selected, setSelected] = useState<{ id: string; style: FreeTextStyle } | null>(null);
    /** Edit text pairs, both ways: cover id ↔ new-text id. Removing one removes the other. */
    const pairsRef = useRef(new Map<string, string>());
    /** An Edit text pair being added — its ids may arrive as annotationAdd events. */
    const pendingPairRef = useRef<{ coverId: string | null; textId: string | null; until: number } | null>(null);
    /** Events from our own programmatic changes until then — whoever made them counts them once. */
    const suppressUntil = useRef(0);
    /** The page organizer was opened from Edit — its Save reloads the document. */
    const pagesPendingRef = useRef(false);
    /** The bytes in the viewer as opened or last saved — Discard reloads them. */
    const lastBlobRef = useRef<Blob | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const setTool = useCallback((t: EditTool) => {
        toolRef.current = t;
        setToolState(t);
    }, []);

    const markDirty = useCallback(() => {
        if (!docRef.current) return;
        if (statusRef.current === "saving") {
            changedWhileSaving.current = true;
            return;
        }
        statusRef.current = "dirty";
        setStatus("dirty");
    }, []);

    /** One more edit on the "N edits" counter (Edit mode only). */
    const countEdit = useCallback(() => {
        if (modeRef.current !== "edit") return;
        editsRef.current += 1;
        setEdits(editsRef.current);
    }, []);

    /** Back to View without touching the document (the caller reloads it when edits must go). */
    const exitEditState = useCallback(() => {
        if (modeRef.current === "edit") viewerRef.current?.exitEditing();
        modeRef.current = "view";
        setMode("view");
        editsRef.current = 0;
        setEdits(0);
        setTool("none");
        setFormOpen(false);
        setHint(null);
        setSelected(null);
        pairsRef.current.clear();
        pendingPairRef.current = null;
        pagesPendingRef.current = false;
    }, [setTool]);

    /** False when `isCurrent` says the open was abandoned (Close) before the bytes reached the viewer. */
    const showInViewer = useCallback(
        async (blob: Blob, isCurrent: () => boolean = () => true) => {
            // Drive may label the bytes octet-stream; the viewer wants a PDF data URL.
            const pdf = new Blob([blob], { type: "application/pdf" });
            const base64 = await blobToBase64(pdf);
            if (!isCurrent()) return false;
            exitEditState();
            lastBlobRef.current = pdf;
            ignoreChangesUntil.current = Date.now() + 2000;
            viewerRef.current?.loadBase64(base64);
            return true;
        },
        [exitEditState]
    );

    const openFromDrive = useCallback(
        async (id: string) => {
            const seq = ++openSeqRef.current;
            const isCurrent = () => seq === openSeqRef.current;
            loadedIdRef.current = id;
            setError(null);
            setLoading(true);
            try {
                const meta = await driveApi.getItem(id, tokenRef.current);
                if (!isCurrent()) return;
                if (meta.kind !== "File") throw new Error(`“${meta.name}” is a folder — pick a PDF file.`);
                if (meta.canDownload === false) {
                    throw new Error(`The owner turned off downloading for “${meta.name}”, so it can't open in the PDF editor. Preview it in Drive instead.`);
                }
                const blob = await driveApi.downloadBlob(id, tokenRef.current);
                if (!(await showInViewer(blob, isCurrent))) return;
                setDoc({ name: meta.name, drive: meta });
                setStatus("clean");
                setFileParam(id);
            } catch (err) {
                if (isCurrent()) setError(messageOf(err));
            } finally {
                if (isCurrent()) setLoading(false);
            }
        },
        [showInViewer, setFileParam]
    );

    // ?fileId= (and the older ?openFile=&fileName= hand-off) once the viewer is up.
    useEffect(() => {
        if (!viewerReady || !hasToken) return;
        if (fileIdParam) {
            if (loadedIdRef.current !== fileIdParam) void openFromDrive(fileIdParam);
            return;
        }
        if (openFileUrl && !urlOpened.current) {
            urlOpened.current = true;
            const seq = ++openSeqRef.current;
            const isCurrent = () => seq === openSeqRef.current;
            void (async () => {
                setError(null);
                setLoading(true);
                try {
                    // The bearer token only ever goes to the Drive BFF.
                    const headers = new Headers();
                    if (tokenRef.current && isDriveUrl(openFileUrl)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                    const res = await fetch(openFileUrl, { headers, credentials: "omit" });
                    if (!res.ok) throw new Error(`Couldn't fetch the file (${res.status} ${res.statusText})`);
                    if (!(await showInViewer(await res.blob(), isCurrent))) return;
                    setDoc({ name: openFileName ?? "document.pdf", drive: null });
                    setStatus("clean");
                } catch (err) {
                    if (isCurrent()) setError(messageOf(err));
                } finally {
                    if (isCurrent()) setLoading(false);
                }
            })();
        }
    }, [viewerReady, hasToken, fileIdParam, openFileUrl, openFileName, openFromDrive, showInViewer]);

    // ---------- Edit tools ----------

    const linkPendingPair = useCallback(() => {
        const p = pendingPairRef.current;
        if (!p?.coverId || !p.textId) return;
        pairsRef.current.set(p.coverId, p.textId);
        pairsRef.current.set(p.textId, p.coverId);
        pendingPairRef.current = null;
    }, []);

    /** Edit text: cover `rect` in white and put an editable text box with `text` on top. One edit. */
    const createPair = useCallback(
        (rect: PageRect, text: string, lineHeight: number) => {
            const v = viewerRef.current;
            if (!v) return;
            const now = Date.now();
            suppressUntil.current = now + 800;
            pendingPairRef.current = { coverId: null, textId: null, until: now + 1500 };
            const ids = v.addCoverAndText(rect, text, lineHeight);
            const p = pendingPairRef.current;
            if (p) {
                p.coverId ??= ids.coverId;
                p.textId ??= ids.textId;
                linkPendingPair();
            }
            v.setTool("None");
            setTool("none");
            markDirty();
            countEdit();
            setHint("Double-click the new text to change it. The bar above it sets font, size, bold and colour.");
            const textId = ids.textId;
            if (textId) window.setTimeout(() => viewerRef.current?.selectAnnotation(textId), 60);
        },
        [countEdit, linkPendingPair, markDirty, setTool]
    );

    /** Edit text by dragging: the plain box just drawn becomes a cover + new text. */
    const replaceRegion = useCallback(
        (drawnId: string, rect: PageRect) => {
            const v = viewerRef.current;
            if (!v) return;
            suppressUntil.current = Date.now() + 800;
            v.setTool("None");
            v.deleteAnnotation(drawnId);
            const under = v.textUnder(rect);
            createPair(rect, under.text, under.lineHeight);
        },
        [createPair]
    );

    /** One placement per click: the tool lets go once the user has placed something. */
    const finishTool = useCallback(
        (resetViewer: boolean) => {
            window.setTimeout(() => {
                if (resetViewer) viewerRef.current?.setTool("None");
                setTool("none");
                setHint(null);
            }, 0);
        },
        [setTool]
    );

    const onEditText = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        if (toolRef.current === "edit-text") {
            v.setTool("None");
            setTool("none");
            setHint(null);
            return;
        }
        const sel = v.lastTextSelection();
        if (sel && sel.page > 0 && sel.width > 0 && sel.height > 0) {
            v.clearTextSelection();
            createPair(sel, sel.text, sel.lineHeight);
            return;
        }
        v.setTool("Rectangle", { cover: true });
        setTool("edit-text");
        setHint("Drag over the words you want to replace — or select them first, then choose Edit text.");
    }, [createPair, setTool]);

    const toggleTool = useCallback(
        (t: EditTool, drawTool: DrawTool, message: string) => {
            const v = viewerRef.current;
            if (!v) return;
            if (toolRef.current === t) {
                v.setTool("None");
                setTool("none");
                setHint(null);
                return;
            }
            v.setTool(drawTool);
            setTool(t);
            setHint(message);
        },
        [setTool]
    );

    const onAddText = useCallback(
        () => toggleTool("add-text", "FreeText", "Click on the page where the text box should go, then type."),
        [toggleTool]
    );

    const onSign = useCallback(
        () => toggleTool("sign", "HandWrittenSignature", "Draw, type or upload your signature, then click where it goes."),
        [toggleTool]
    );

    const onShape = useCallback(
        (kind: ShapeKind) => {
            const v = viewerRef.current;
            if (!v) return;
            v.setTool(kind);
            setTool("shape");
            const what = kind === "Circle" ? "an ellipse" : kind === "Arrow" ? "an arrow" : `a ${kind.toLowerCase()}`;
            setHint(`Drag on the page to draw ${what}.`);
        },
        [setTool]
    );

    const onFormFields = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        const next = !formOpen;
        v.setTool("None");
        setTool("none");
        v.setFormDesigner(next);
        setFormOpen(next);
        setHint(next ? "Pick a field type in the form toolbar, then draw it on the page. Choose Form fields again when you're done." : null);
    }, [formOpen, setTool]);

    const onPages = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        v.setTool("None");
        setTool("none");
        if (!v.openPageOrganizer()) {
            setError("The page organizer isn't available.");
            return;
        }
        pagesPendingRef.current = true;
        setHint("Reorder, rotate, delete or insert pages, then choose Save in the organizer.");
    }, [setTool]);

    const onImagePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (imageInputRef.current) imageInputRef.current.value = "";
        if (!file) return;
        if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
            setError(`${file.name}: pick a PNG or JPEG image.`);
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setError(`${file.name} is over ${MAX_IMAGE_BYTES / 1024 / 1024} MB — pick a smaller image.`);
            return;
        }
        try {
            const dataUrl = await blobToBase64(file);
            const size = await imageSize(dataUrl);
            const v = viewerRef.current;
            if (!v || modeRef.current !== "edit") return;
            v.setTool("None");
            setTool("none");
            suppressUntil.current = Date.now() + 800;
            v.addImage(dataUrl, size.width, size.height, file.name);
            markDirty();
            countEdit();
            setHint("Drag the image to move it; drag a corner to resize it.");
        } catch (err) {
            setError(`Couldn't place the image: ${messageOf(err)}`);
        }
    };

    // ---------- Viewer events ----------

    const onDocumentLoad = useCallback(() => {
        ignoreChangesUntil.current = Date.now() + 600;
        setSelected(null);
        if (pagesPendingRef.current && docRef.current) {
            // The page organizer applies its changes by reloading the document.
            pagesPendingRef.current = false;
            markDirty();
            countEdit();
        }
    }, [countEdit, markDirty]);

    const onDocumentLoadFailed = useCallback(() => {
        const name = docRef.current?.name;
        exitEditState();
        setDoc(null);
        setStatus("clean");
        setError(`Couldn't open ${name ? `“${name}”` : "this file"} — it may not be a PDF, or it may be damaged.`);
    }, [exitEditState]);

    const onDocumentChange = useCallback(
        (change: ViewerChange) => {
            const now = Date.now();
            if (now < ignoreChangesUntil.current || !docRef.current) return;
            const counted = now >= suppressUntil.current;

            const pending = pendingPairRef.current;
            if (pending && change.kind === "annotationAdd" && change.annotationId) {
                if (now > pending.until) {
                    pendingPairRef.current = null;
                } else if (change.subject === EDIT_COVER_SUBJECT) {
                    pending.coverId ??= change.annotationId;
                    linkPendingPair();
                } else if (change.annotationType === "FreeText") {
                    pending.textId ??= change.annotationId;
                    linkPendingPair();
                }
            }

            if (modeRef.current === "edit") {
                const t = toolRef.current;
                const drawnId = change.annotationId;
                const drawnRect = change.rect;
                if (
                    change.kind === "annotationAdd" && t === "edit-text" && counted &&
                    change.annotationType === "Rectangle" && change.subject !== EDIT_COVER_SUBJECT &&
                    drawnId && drawnRect
                ) {
                    // Counted once, by createPair.
                    window.setTimeout(() => replaceRegion(drawnId, drawnRect), 0);
                    return;
                }
                if (change.kind === "annotationAdd" && counted) {
                    if (t === "add-text") finishTool(false); // the new box is being typed in — leave the viewer be
                    else if (t === "shape") finishTool(true);
                }
                if (change.kind === "signature" && t === "sign") finishTool(false);
            }

            // Removing half of an Edit text pair removes the other half (one edit).
            if (change.kind === "annotationRemove" && change.annotationId) {
                const partner = pairsRef.current.get(change.annotationId);
                if (partner) {
                    pairsRef.current.delete(change.annotationId);
                    pairsRef.current.delete(partner);
                    suppressUntil.current = now + 600;
                    window.setTimeout(() => viewerRef.current?.deleteAnnotation(partner), 0);
                }
                if (selected?.id === change.annotationId) setSelected(null);
            }

            markDirty();
            if (counted) countEdit();
        },
        [countEdit, finishTool, linkPendingPair, markDirty, replaceRegion, selected]
    );

    const onViewerReady = useCallback(() => setViewerReady(true), []);

    // ---------- Mini toolbar for the selected text box ----------

    const onAnnotationSelect = useCallback((id: string | null) => {
        if (!id || modeRef.current !== "edit") {
            setSelected(null);
            return;
        }
        const style = viewerRef.current?.freeTextStyle(id) ?? null;
        setSelected(style ? { id, style } : null);
    }, []);

    const selectedId = selected?.id ?? null;
    const getSelectedRect = useCallback(
        () => (selectedId ? viewerRef.current?.annotationClientRect(selectedId) ?? null : null),
        [selectedId]
    );

    const editSelectedText = useCallback(
        (patch: Partial<FreeTextStyle>) => {
            const v = viewerRef.current;
            if (!v || !selected) return;
            suppressUntil.current = Date.now() + 500;
            v.editFreeText(selected.id, patch);
            markDirty();
            countEdit();
            setSelected({ id: selected.id, style: { ...selected.style, ...patch } });
        },
        [countEdit, markDirty, selected]
    );

    const removeSelectedText = useCallback(() => {
        if (selected) viewerRef.current?.deleteAnnotation(selected.id);
        setSelected(null);
    }, [selected]);

    // ---------- Comments (a Drive file only; anyone who can open it) ----------

    const driveId = doc?.drive?.id ?? null;
    const [commentsOpen, setCommentsOpen] = useState(false);
    const commentCount = useOpenCommentCount(driveId);
    const commentsAdapter = useMemo(() => pdfCommentsAdapter(() => viewerRef.current), []);
    const openComments = useCallback(() => {
        if (!docRef.current?.drive) {
            setError("Save this PDF to Drive to comment on it.");
            return;
        }
        setCommentsOpen(true);
    }, []);
    useEffect(() => {
        if (!driveId) setCommentsOpen(false);
    }, [driveId]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!isCommentsShortcut(e)) return;
            e.preventDefault();
            openComments();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openComments]);

    // ---------- Open ----------

    const confirmDiscard = useCallback(
        () =>
            statusRef.current !== "dirty" ||
            window.confirm(`Discard your unsaved changes to “${docRef.current?.name ?? "this PDF"}”?`),
        []
    );

    const startOpen = useCallback(() => {
        if (confirmDiscard()) setDialog({ kind: "open-choice" });
    }, [confirmDiscard]);

    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file) return;
        if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
            setError(`${file.name}: only .pdf files can be opened here.`);
            return;
        }
        try {
            loadedIdRef.current = null;
            await showInViewer(file);
            setDoc({ name: file.name, drive: null });
            setStatus("clean");
            setError(null);
            setFileParam(null);
        } catch (err) {
            setError(messageOf(err));
        }
    };

    // ---------- Save ----------

    const runSave = useCallback(
        async (upload: (pdf: Blob) => Promise<DriveItem>) => {
            const viewer = viewerRef.current;
            const current = docRef.current;
            if (!viewer || !current) {
                setError("Nothing to save — open a PDF first.");
                return;
            }
            // Edit mode: the edits are burned into the page before they're saved.
            const burnIn = modeRef.current === "edit";
            statusRef.current = "saving";
            setStatus("saving");
            setError(null);
            changedWhileSaving.current = false;
            let settle = () => {};
            saveInFlightRef.current = new Promise<void>((resolve) => { settle = resolve; });
            try {
                // The document as it is now — annotations, form values, signatures.
                const exported = new Blob([await viewer.saveAsBlob()], { type: "application/pdf" });
                const pdf = burnIn ? await driveApi.flattenPdf(exported, current.name, tokenRef.current) : exported;
                const saved = await upload(pdf);
                loadedIdRef.current = saved.id;
                setDoc({ name: saved.name, drive: saved });
                setFileParam(saved.id);
                const next: SaveStatus = changedWhileSaving.current ? "dirty" : "clean";
                if (burnIn && next === "clean") {
                    // Show what was saved — the edits are part of the page now — and go back to View.
                    await showInViewer(pdf);
                } else {
                    // Still editing (changed during the save): Discard now goes back to the saved version.
                    lastBlobRef.current = pdf;
                }
                // Set the ref now too: Close reads it right after this save settles.
                statusRef.current = next;
                setStatus(next);
            } catch (err) {
                statusRef.current = "failed";
                setStatus("failed");
                setError(`Couldn't save to Drive: ${messageOf(err)}`);
            } finally {
                saveInFlightRef.current = null;
                settle();
            }
        },
        [setFileParam, showInViewer]
    );

    /** Save to Drive: a new version when the user may edit the file, otherwise Save As. */
    const save = useCallback(() => {
        const d = docRef.current;
        if (!d) {
            setError("Nothing to save — open a PDF first.");
            return;
        }
        if (statusRef.current === "saving") return;
        const target = d.drive;
        if (target && canSaveInPlace(target)) {
            void runSave((pdf) => driveApi.uploadVersion(target.id, pdf, target.name, tokenRef.current));
        } else {
            setDialog({ kind: "save-as", target: "drive" });
        }
    }, [runSave]);

    const onSaveAs = async (choice: SaveAsChoice) => {
        setDialog(null);
        const fileName = `${choice.name}.pdf`;
        if (choice.target === "computer") {
            try {
                const viewer = viewerRef.current;
                if (!viewer) throw new Error("Open a PDF first.");
                const exported = new Blob([await viewer.saveAsBlob()], { type: "application/pdf" });
                const pdf = modeRef.current === "edit"
                    ? await driveApi.flattenPdf(exported, fileName, tokenRef.current)
                    : exported;
                saveBlobToComputer(pdf, fileName);
            } catch (err) {
                setError(`Couldn't download: ${messageOf(err)}`);
            }
            return;
        }
        await runSave((pdf) => driveApi.uploadBlob(pdf, fileName, choice.folderId, tokenRef.current));
    };

    // ---------- View / Edit ----------

    const enterEdit = useCallback(() => {
        const v = viewerRef.current;
        if (modeRef.current === "edit" || !v || !docRef.current) return;
        modeRef.current = "edit";
        setMode("edit");
        editsRef.current = 0;
        setEdits(0);
        pairsRef.current.clear();
        setSelected(null);
        v.setEditLook(true);
        setHint("Pick a tool. Your edits are burned into the PDF when you save to Drive.");
    }, []);

    /** Reloads the version last opened or saved — every unsaved change goes. */
    const revert = useCallback(async () => {
        const blob = lastBlobRef.current;
        if (!blob) {
            exitEditState();
            return;
        }
        try {
            await showInViewer(blob);
            changedWhileSaving.current = false;
            statusRef.current = "clean";
            setStatus("clean");
        } catch (err) {
            setError(`Couldn't discard the edits: ${messageOf(err)}`);
        }
    }, [exitEditState, showInViewer]);

    /** False when the user chose to keep editing. */
    const leaveEdit = useCallback(async (): Promise<boolean> => {
        if (modeRef.current !== "edit") return true;
        const n = editsRef.current;
        if (n === 0) {
            exitEditState();
            return true;
        }
        if (!window.confirm(`Discard ${plural(n, "edit")} and go back to View? Edits are only kept when you save them to Drive.`)) {
            return false;
        }
        await revert();
        return true;
    }, [exitEditState, revert]);

    const discardEdits = useCallback(async () => {
        const n = editsRef.current;
        if (n === 0) {
            exitEditState();
            return;
        }
        if (!window.confirm(`Discard ${plural(n, "edit")}? The PDF goes back to how it was when you opened or last saved it.`)) return;
        await revert();
    }, [exitEditState, revert]);

    const onModeChange = useCallback(
        (next: PdfMode) => {
            if (next === "edit") enterEdit();
            else void leaveEdit();
        },
        [enterEdit, leaveEdit]
    );

    // Esc lets go of the tool being placed.
    useEffect(() => {
        if (mode !== "edit") return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || toolRef.current === "none") return;
            viewerRef.current?.setTool("None");
            setTool("none");
            setHint(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mode, setTool]);

    // ---------- Close ----------

    // PDF has no separate home: Close unloads the document and drops
    // ?fileId= / ?openFile= (replace), which brings back the "Open a PDF"
    // start screen. A save in flight finishes first — never cut off; changes
    // still unsaved after it (or a failed save) ask first.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            if (saveInFlightRef.current) await saveInFlightRef.current;
            const s = statusRef.current;
            if (
                docRef.current &&
                (s === "dirty" || s === "failed" || s === "saving") &&
                !window.confirm(`You have unsaved changes to “${docRef.current.name}”. Close without saving?`)
            ) {
                return;
            }
            openSeqRef.current += 1; // an open still in progress drops its result
            loadedIdRef.current = null;
            urlOpened.current = true;
            docRef.current = null;
            ignoreChangesUntil.current = Date.now() + 1000;
            exitEditState();
            viewerRef.current?.unload();
            lastBlobRef.current = null;
            changedWhileSaving.current = false;
            statusRef.current = "clean";
            setDoc(null);
            setStatus("clean");
            setLoading(false);
            setError(null);
            setDialog(null);
            setCommentsOpen(false);
            setFileParam(null);
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [exitEditState, setFileParam]);

    // Ctrl+S saves, Ctrl+Shift+S is Save as — ahead of the viewer's own keys.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() !== "s" || !(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            e.stopPropagation();
            if (!docRef.current) return;
            if (e.shiftKey) setDialog({ kind: "save-as", target: "drive" });
            else save();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [save]);

    // Leaving with unsaved changes asks first.
    useEffect(() => {
        if (status !== "dirty" && status !== "saving") return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [status]);

    // The viewer's own Open / Download toolbar buttons use our dialogs.
    const onViewerToolbarClick = useCallback(
        (args: unknown) => {
            try {
                const a = args as { item?: { id?: string; tooltipText?: string }; cancel?: boolean };
                const id = (a?.item?.id ?? "").toLowerCase();
                const tip = (a?.item?.tooltipText ?? "").toLowerCase();
                if (id.includes("open") || tip === "open") {
                    a.cancel = true;
                    startOpen();
                    return;
                }
                if (id.includes("download") || tip === "download") {
                    a.cancel = true;
                    if (docRef.current) setDialog({ kind: "save-as", target: "computer" });
                }
            } catch {
                /* swallow */
            }
        },
        [startOpen]
    );

    const drive = doc?.drive ?? null;
    const inPlace = drive ? canSaveInPlace(drive) : false;
    const startFolderId = drive && !drive.ownerEmail ? drive.parentId : null;
    // Something to close: a document, one opening, or a failed ?fileId= open.
    const canClose = Boolean(doc || loading || fileIdParam || openFileUrl);
    const saving = status === "saving";
    // A view-only shared file can't be edited — Save as makes an own copy first.
    const canEdit = Boolean(doc) && !loading && !saving && (!drive || inPlace);
    const editTitle = !doc
        ? "Open a PDF to edit it"
        : drive && !inPlace
            ? `View only — ${drive.ownerEmail ?? "the owner"} shared this with view access. Save as… to edit your own copy.`
            : "Edit text, add images, shapes, signatures and form fields, and organise pages";

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" hidden onChange={onLocalPick} />
            <input ref={imageInputRef} type="file" accept="image/png,image/jpeg" hidden onChange={onImagePicked} />

            {/* File name + save status live in the suite top bar's title region. */}
            <ShellHeaderPortal>
                <span className="min-w-0 truncate text-sm font-medium" title={doc?.name}>
                    {doc?.name ?? (loading ? "Opening…" : "No PDF open")}
                </span>
                <span aria-live="polite" className="hidden shrink-0 items-center sm:flex">
                    <StatusBadge status={status} loading={loading} doc={doc} inPlace={inPlace} onRetry={save} />
                </span>
                {/* Close — the last editor control, as in Docs / Sheets / Slides. */}
                {canClose ? (
                    <div className="suite-appbar-divider ml-auto flex shrink-0 items-center pl-1.5">
                        <button
                            type="button"
                            onClick={() => void closeFile()}
                            disabled={closing}
                            aria-label="Close file"
                            aria-busy={closing || undefined}
                            title={closing ? "Finishing the save before closing…" : "Close file (return to PDF home)"}
                            className="suite-appbar-btn flex h-8 w-8 items-center justify-center rounded disabled:cursor-wait"
                        >
                            {closing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
                        </button>
                    </div>
                ) : null}
            </ShellHeaderPortal>

            {/* File bar: View / Edit, then Open / Save to Drive / Save as / Comments. */}
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b bg-background px-3 py-1.5">
                <ModeSwitch
                    mode={mode}
                    onChange={onModeChange}
                    editDisabled={!canEdit}
                    viewDisabled={saving}
                    editTitle={editTitle}
                />
                <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
                <div className="flex flex-wrap items-center gap-1">
                    <BarButton icon={FolderOpen} onClick={startOpen} title="Open a PDF from Drive or this computer">
                        Open
                    </BarButton>
                    <BarButton
                        icon={Save}
                        primary={mode === "view"}
                        onClick={save}
                        disabled={!doc || saving}
                        title={
                            mode === "edit"
                                ? "Burn the edits into the PDF and save it to Drive (Ctrl+S)"
                                : inPlace ? "Save a new version to Drive (Ctrl+S)" : "Save a copy to Drive (Ctrl+S)"
                        }
                    >
                        Save to Drive
                    </BarButton>
                    <BarButton
                        icon={CopyPlus}
                        onClick={() => setDialog({ kind: "save-as", target: "drive" })}
                        disabled={!doc || saving}
                        title="Save as a new file (Ctrl+Shift+S)"
                    >
                        Save as…
                    </BarButton>
                    <CommentsButton
                        file={drive}
                        tone="surface"
                        openCount={commentCount.count}
                        active={commentsOpen}
                        onClick={commentsOpen ? () => setCommentsOpen(false) : openComments}
                    />
                    {drive ? (
                        <a
                            href={`${DRIVE_WEB_URL.replace(/\/$/, "")}/open/${encodeURIComponent(drive.id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Show this file in Drive"
                            className="hidden h-8 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground md:flex"
                        >
                            <ExternalLink className="h-4 w-4" aria-hidden /> Drive
                        </a>
                    ) : null}
                    <BarButton
                        icon={X}
                        onClick={() => void closeFile()}
                        disabled={!canClose || closing}
                        title="Close this PDF (return to the start screen)"
                    >
                        Close
                    </BarButton>
                </div>
                {mode === "edit" ? (
                    <span className="pdf-edit-text ml-auto flex shrink-0 items-center gap-1.5 text-xs font-semibold" aria-live="polite">
                        <PencilLine className="h-3.5 w-3.5" aria-hidden />
                        {plural(edits, "edit")}
                    </span>
                ) : null}
            </div>

            {mode === "edit" ? (
                <EditToolbar
                    tool={tool}
                    formOpen={formOpen}
                    edits={edits}
                    saving={saving}
                    inPlace={inPlace}
                    hint={hint}
                    onEditText={onEditText}
                    onAddText={onAddText}
                    onImage={() => imageInputRef.current?.click()}
                    onShape={onShape}
                    onSign={onSign}
                    onFormFields={onFormFields}
                    onPages={onPages}
                    onDiscard={() => void discardEdits()}
                    onSave={save}
                />
            ) : null}

            {error ? (
                <div
                    role="alert"
                    className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                >
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span className="flex-1">{error}</span>
                    {!doc && fileIdParam && !loading ? (
                        <button
                            type="button"
                            onClick={() => void openFromDrive(fileIdParam)}
                            className="shrink-0 font-medium underline-offset-2 hover:underline"
                        >
                            Try again
                        </button>
                    ) : null}
                    <button type="button" onClick={() => setError(null)} aria-label="Dismiss" className="shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            <div className="relative min-h-0 flex-1">
                <Suspense
                    fallback={
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading viewer…
                        </div>
                    }
                >
                    <PdfViewerHost
                        ref={viewerRef}
                        onReady={onViewerReady}
                        onToolbarClick={onViewerToolbarClick}
                        onDocumentLoad={onDocumentLoad}
                        onDocumentLoadFailed={onDocumentLoadFailed}
                        onDocumentChange={onDocumentChange}
                        onAnnotationSelect={onAnnotationSelect}
                    />
                </Suspense>
                {viewerReady && !doc && !loading && !fileIdParam && !openFileUrl ? (
                    <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center px-4">
                        <div className="pointer-events-auto w-full max-w-sm rounded-xl border bg-card p-6 text-center shadow-lg">
                            <FileType2 className="mx-auto h-10 w-10 text-red-600 dark:text-red-400" aria-hidden />
                            <h2 className="mt-3 text-base font-semibold">Open a PDF</h2>
                            <p className="mt-1 text-sm text-muted-foreground">View, annotate, fill in, sign and edit — then save it back to Drive.</p>
                            <div className="mt-4 flex flex-wrap justify-center gap-2">
                                <BarButton icon={HardDrive} primary onClick={() => setDialog({ kind: "drive-picker" })}>From Drive</BarButton>
                                <BarButton icon={Monitor} onClick={() => fileInputRef.current?.click()}>From this computer</BarButton>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            {mode === "edit" && selected ? (
                <FreeTextMiniToolbar
                    key={selected.id}
                    getRect={getSelectedRect}
                    style={selected.style}
                    onChange={editSelectedText}
                    onRemove={removeSelectedText}
                />
            ) : null}

            {commentsOpen && drive ? (
                <CommentsPanel
                    key={drive.id}
                    file={{ id: drive.id, name: drive.name }}
                    adapter={commentsAdapter}
                    onClose={() => setCommentsOpen(false)}
                    onOpenCountChange={commentCount.set}
                />
            ) : null}

            {dialog?.kind === "open-choice" ? (
                <OpenChoiceDialog
                    onClose={() => setDialog(null)}
                    onPickComputer={() => {
                        setDialog(null);
                        fileInputRef.current?.click();
                    }}
                    onPickDrive={() => setDialog({ kind: "drive-picker" })}
                />
            ) : null}
            {dialog?.kind === "drive-picker" ? (
                <DrivePickerDialog
                    onClose={() => setDialog(null)}
                    onPick={(item) => {
                        setDialog(null);
                        void openFromDrive(item.id);
                    }}
                />
            ) : null}
            {dialog?.kind === "save-as" ? (
                <SaveAsDialog
                    defaultName={stripExtension(doc?.name ?? "Untitled")}
                    initialTarget={dialog.target}
                    startFolderId={startFolderId}
                    onClose={() => setDialog(null)}
                    onConfirm={(choice) => void onSaveAs(choice)}
                />
            ) : null}
        </div>
    );
}

function BarButton({
    icon: Icon, children, onClick, disabled, primary, title
}: {
    icon: LucideIcon;
    children: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    primary?: boolean;
    title?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
                primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "hover:bg-accent"
            )}
        >
            <Icon className="h-4 w-4" aria-hidden />
            {children}
        </button>
    );
}

/**
 * "Saved to Drive" / "Saving…" / "Unsaved changes" — as in Docs, Sheets and Slides.
 * Rendered in the suite top bar, so colours come from the bar (accent, light or
 * dark style): opacity for secondary text, suite-appbar-btn for the retry button.
 */
function StatusBadge({
    status, loading, doc, inPlace, onRetry
}: {
    status: SaveStatus;
    loading: boolean;
    doc: OpenDoc | null;
    inPlace: boolean;
    onRetry: () => void;
}) {
    const base = "flex items-center gap-1 whitespace-nowrap text-xs opacity-80";
    if (loading) return <span className={base}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Opening…</span>;
    if (!doc) return null;
    switch (status) {
        case "saving":
            return <span className={base}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Saving…</span>;
        case "failed":
            return (
                <button
                    type="button"
                    onClick={onRetry}
                    className="suite-appbar-btn flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium underline-offset-2 hover:underline"
                >
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />Save failed — retry
                </button>
            );
        case "dirty":
            return <span className={base}><CircleDot className="h-3.5 w-3.5" aria-hidden />Unsaved changes</span>;
        default:
            if (!doc.drive) return <span className={base}>Not saved to Drive</span>;
            return inPlace
                ? <span className={base}><Check className="h-3.5 w-3.5" aria-hidden />Saved to Drive</span>
                : <span className={base} title={`Shared by ${doc.drive.ownerEmail ?? "someone else"}`}>View only — Save as to keep a copy</span>;
    }
}

function saveBlobToComputer(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
        reader.readAsDataURL(blob);
    });
}

/** Natural pixel size of an image data URL. */
function imageSize(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth || 200, height: img.naturalHeight || 200 });
        img.onerror = () => reject(new Error("That image couldn't be read."));
        img.src = dataUrl;
    });
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
